import * as React from "react"
import { Icon as Iconify } from "@iconify/react"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"
import { AlertTriangle } from "lucide-react"

const iconVariants = cva(
  "inline-block shrink-0 transition-colors duration-200",
  {
    variants: {
      color: {
        default: "text-foreground",
        muted: "text-muted-foreground",
        secondary: "text-secondary-foreground",
        primary: "text-primary",
        success: "text-success",
        info: "text-info",
        warning: "text-warning",
        danger: "text-danger",
        white: "text-white",
        dark: "text-dark",
      },
      size: {
        xs: "h-3 w-3",
        sm: "h-4 w-4",
        md: "h-5 w-5",
        lg: "h-6 w-6",
        xl: "h-8 w-8",
        "2xl": "h-10 w-10",
        "3xl": "h-12 w-12",
      },
    },
    defaultVariants: {
      color: "default",
      size: "md",
    },
  }
)

const Icon = React.forwardRef(
  ({ icon, color, size, className, ...props }, ref) => {
    // Determine the base classes using the CVA variants
    const resolvedClass = cn(iconVariants({ color, size }), className)

    // Fallback icon if none is provided
    if (!icon) {
      return (
        <Iconify
          icon={AlertTriangle}
          className={resolvedClass}
          ref={ref}
          {...props}
        />
      )
    }

    // If icon is a string, render it using Iconify
    if (typeof icon === "string") {
      return (
        <Iconify
          icon={icon}
          className={resolvedClass}
          ref={ref}
          {...props}
        />
      )
    }

    // If icon is a React element/component (e.g. Lucide or React-Icons)
    if (typeof icon === "function" || typeof icon === "object") {
      return React.createElement(icon, {
        className: resolvedClass,
        ref,
        ...props,
      })
    }

    // Fallback generic renderer
    return (
      <Iconify
        icon={AlertTriangle}
        className={resolvedClass}
        ref={ref}
        {...props}
      />
    )
  }
)

Icon.displayName = "Icon"

export { Icon, iconVariants }
